import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TProjectOptionsProps } from '@/console/features/projects/hooks/use-project-select-options/use-project-select-options.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { IdeVersionLabel } from '@/shared/components/ui/ide-version-label/ide-version-label';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import {
  ProjectForSelectFragment,
  ProjectTypeEnum,
} from '@/shared/types/types.generated';

export const useProjectSelectOptions = (props?: TProjectOptionsProps) => {
  const { t } = useTranslation();
  const { getVendor, vendors, getIde, getFeature, getProjectTypesForFeature } =
    useVendor();
  const { additionalInfo, disabled } = props ?? {};
  const feature = props?.feature;

  const projectTypesForFeature = !feature
    ? Object.values(ProjectTypeEnum)
    : [
        ...new Set(
          vendors
            .flatMap((vendor) => vendor.ides ?? [])
            .flatMap((ide) => ide.features)
            .filter(({ feature: featureName }) => featureName === feature)
            .flatMap((featureConfig) =>
              getProjectTypesForFeature(featureConfig),
            ),
        ),
      ];

  const noLinkedDevice = (project: ProjectForSelectFragment) => {
    return disabled?.noLinkedDevice && project.linkedDevices.itemCount === 0;
  };

  const noCompatibleDevice = (project: ProjectForSelectFragment) => {
    return Boolean(disabled?.noCompatibleDevice?.(project));
  };

  const getAdditionalInfo = (project: ProjectForSelectFragment) => {
    if (disabled?.notYetSupported?.(project)) {
      return t('common:not_yet_supported');
    }

    if (noLinkedDevice(project)) {
      return t('common:no_linked_device');
    }

    if (noCompatibleDevice(project)) {
      return t('device:no_compatible_device_available');
    }

    const infos: ReactNode[] = [];
    if (additionalInfo?.vendor && getVendor(project)?.name) {
      infos.push(getVendor(project)?.name);
    }
    if (
      additionalInfo?.ide &&
      getIde({
        vendorId: project.vendorId,
        ideConfigId: project.ideConfigId,
      })?.name
    ) {
      infos.push(
        <IdeVersionLabel
          key="ide"
          vendorId={project.vendorId}
          ideConfigId={project.ideConfigId}
          variant="inherit"
        />,
      );
    }
    if (additionalInfo?.projectType) {
      infos.push(t(`projects:${project.projectType.toLowerCase()}`));
    }
    return infos;
  };

  const isDisabled = (project: ProjectForSelectFragment) => {
    if (disabled?.notYetSupported?.(project)) {
      return true;
    }

    if (noLinkedDevice(project)) {
      return true;
    }

    if (noCompatibleDevice(project)) {
      return true;
    }

    return false;
  };

  const createProjectOption = (project: ProjectForSelectFragment) => {
    return {
      label: project.name,
      value: {
        name: project.name,
        projectId: project.projectId,
        objectVersion: project.objectVersion ?? 0,
      },
      additionalInfo: getAdditionalInfo(project),
      disabled: isDisabled(project),
    } as TSelectOption<
      Omit<ProjectForSelectFragment, 'objectVersion'> & {
        objectVersion: number;
      }
    >;
  };

  const shouldFilterProjectsForFeature =
    Object.values(ProjectTypeEnum).length !== projectTypesForFeature?.length;

  const projectFeatureFilter = (project: ProjectForSelectFragment) => {
    if (!shouldFilterProjectsForFeature || !feature) {
      return true;
    }

    return !!getFeature({
      vendorId: project.vendorId,
      ideConfigId: project.ideConfigId,
      feature,
      projectType: project.projectType,
    });
  };

  const notYetSupported = (project: ProjectForSelectFragment) => {
    return !getIde(project)?.features.some(
      ({ feature: featureName }) => featureName === feature,
    );
  };

  const getProjectSelectHint = () => {
    if (!shouldFilterProjectsForFeature || !props?.feature) {
      return undefined;
    }

    return t('pipelines:project_device_step_options.project_select_hint', {
      projectTypes: projectTypesForFeature
        ?.map((type) =>
          t(`projects:form.fields.project_type.${type.toLowerCase()}`),
        )
        .join(', '),
      feature: t(
        `pipelines:pipeline_steps_enum.${props?.feature?.toLowerCase()}`,
      ),
    });
  };

  return {
    createProjectOption,
    getAdditionalInfo,
    isDisabled,
    projectFeatureFilter,
    notYetSupported,
    projectSelectHint: getProjectSelectHint(),
  };
};
